/* VEXiAL — el pop-up de agregar rápido
   ============================================================
   Tres piezas sobre el quick-add nativo de Horizon (assets/quick-add.js),
   que no se toca:

   1. EL PEDIDO. quick-add.js hace fetch a `productPageUrl` y recorta de
      esa respuesta el nodo [data-product-grid-content] para llenar el
      modal. En templates/product.json la ficha la dibuja
      custom-product-vexial y la sección nativa está deshabilitada a
      propósito, así que ahí ese nodo no existe. Agregando ?view=horizon
      Shopify sirve templates/product.horizon.json, que solo contiene
      sections/vx-quick-add.liquid: liviano y con el nodo que el modal
      espera.

      Sin esto el fetch no encuentra el nodo, el modal se abre igual
      (quick-add.js llama a #openQuickAddModal() fuera del
      `if (gridElement)`) y el usuario ve un recuadro blanco.

      Vivía dentro de collection-vexial.liquid limitado a .vxc-results,
      así que en el home (destacados-vexial) no llegaba a instalarse.
      Ahora es global y sin filtro por sección.

   2. EL PINTADO. updateQuickAddModal() reordena el contenido en móvil
      pensando en la sección nativa: saca el precio, el selector y el
      formulario de .product-details y borra el resto. Con la vista de
      vx-quick-add eso dejaba el formulario suelto al final. Para esa
      vista el contenido pasa tal cual y lo ordena el CSS
      (snippets/vx-quick-add-estilos.liquid).

   3. EL COMPORTAMIENTO. Selectores, fotos, cantidad, «Comprar ahora» y
      lista de deseos, con las variantes del JSON de la vista: cambiar de
      variante no pide nada al servidor. Es lo que hace la ficha
      (assets/custom-product-vexial.js), sin sus IDs ni sus globales,
      porque el modal puede abrirse encima de una ficha.
   ============================================================ */
(() => {
  const VIEW = 'horizon';
  const MODAL = 'quick-add-modal-content';

  /* ── 1 y 2. El pedido y el pintado ─────────────────────────── */
  customElements.whenDefined('quick-add-component').then(() => {
    const QuickAdd = customElements.get('quick-add-component');
    if (!QuickAdd) return;

    const descriptor = Object.getOwnPropertyDescriptor(QuickAdd.prototype, 'productPageUrl');
    if (descriptor?.get) {
      const originalGet = descriptor.get;

      Object.defineProperty(QuickAdd.prototype, 'productPageUrl', {
        get() {
          const url = originalGet.call(this);
          if (!url) return url;

          try {
            const parsed = new URL(url, window.location.origin);
            parsed.searchParams.set('view', VIEW);
            return parsed.toString();
          } catch {
            return url;
          }
        },
        configurable: true,
      });
    }

    const pintarNativo = QuickAdd.prototype.updateQuickAddModal;
    if (typeof pintarNativo !== 'function') return;

    QuickAdd.prototype.updateQuickAddModal = async function (productGrid) {
      const vista = productGrid?.querySelector('.vxqa');
      if (!vista) return pintarNativo.call(this, productGrid);

      const destino = document.getElementById(MODAL);
      if (!destino) return;

      destino.setAttribute('view-event-payload', productGrid.getAttribute('view-event-payload') || '');
      destino.replaceChildren(...Array.from(productGrid.childNodes));
      iniciar(destino.querySelector('.vxqa'));
    };
  });

  /* ── 3. El comportamiento ──────────────────────────────────── */
  function cerrarSelectores(raiz, devolverFoco) {
    if (!raiz) return;
    raiz.querySelectorAll('.vxqa-sel.is-open').forEach((sel) => {
      sel.classList.remove('is-open', 'vxqa-sel--arriba');
      const boton = sel.querySelector('[data-vxqa-abrir]');
      if (!boton) return;
      boton.setAttribute('aria-expanded', 'false');
      if (devolverFoco) boton.focus();
    });
  }

  function iniciar(raiz) {
    if (!raiz) return;

    let datos;
    try {
      datos = JSON.parse(raiz.querySelector('script[data-vxqa-datos]').textContent);
    } catch (error) {
      return;
    }

    const variantes = Array.isArray(datos.variantes) ? datos.variantes : [];
    const textos = datos.textos || {};
    const opciones = { option1: null, option2: null, option3: null, ...datos.opts };
    const q = (selector) => raiz.querySelector(selector);
    const componente = q('product-form-component');
    const formulario = componente ? componente.querySelector('form') : null;
    const campoId = q('input[name="id"]');
    const dialogo = raiz.closest('dialog');
    let variante = variantes.find((v) => String(v.id) === String(datos.actual)) || null;
    let foto = -1;
    let relojError = 0;

    if (dialogo && q('#vxqa-titulo')) dialogo.setAttribute('aria-labelledby', 'vxqa-titulo');

    const buscar = (o) =>
      variantes.find((v) => v.option1 === o.option1 && v.option2 === o.option2 && v.option3 === o.option3) || null;

    /* El precio, el tachado y el porcentaje en las dos monedas. Liquid
       publica los céntimos en soles de cada variante, con el precio fijado a
       mano si lo hay, y VXMoney arma el mismo marcado que el snippet
       (MONEDA.md §6.1). Con la moneda apagada, el texto en dólares tal cual. */
    const dinero = () => window.VXMoney;
    const precioHtml = (v) =>
      dinero() ? dinero().html(v.price, { usd: v.price_money, pen: v.pen, fuente: v.pen_src }) : v.price_money;
    const tachadoHtml = (v) =>
      dinero()
        ? dinero().html(v.compare_at_price, { usd: v.compare_money, pen: v.pen_cmp, fuente: v.pen_src })
        : v.compare_money;
    const descuentoHtml = (v) => {
      if (dinero()) {
        return dinero().discountHtml(v.price, v.compare_at_price, { pen: v.pen, penCompare: v.pen_cmp, fuente: v.pen_src });
      }
      return `-${Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price)}%`;
    };

    /* Tacha las opciones que llevan a una combinación inexistente o agotada,
       como la ficha. */
    function marcarOpciones() {
      raiz.querySelectorAll('.vxqa-sel[data-vxqa-opcion]').forEach((sel) => {
        const clave = sel.dataset.vxqaOpcion;
        sel.querySelectorAll('.vxqa-sel-opt').forEach((opcion) => {
          const v = buscar({ ...opciones, [clave]: opcion.dataset.vxqaValor });
          opcion.classList.toggle('is-missing', !v);
          opcion.classList.toggle('is-soldout', !!v && !v.available);
        });
      });
    }

    function marcarDeseo(activo, latir) {
      const boton = q('[data-vxqa-deseo]');
      if (!boton) return;
      boton.classList.toggle('is-active', activo);
      boton.setAttribute('aria-pressed', activo ? 'true' : 'false');
      if (!latir || !activo) return;
      boton.classList.remove('is-latiendo');
      void boton.offsetWidth;
      boton.classList.add('is-latiendo');
      window.setTimeout(() => boton.classList.remove('is-latiendo'), 420);
    }

    function srcsetDe(src) {
      if (!src || !/width=\d+/.test(src)) return '';
      return [400, 600, 800].map((ancho) => `${src.replace(/width=\d+/, `width=${ancho}`)} ${ancho}w`).join(', ');
    }

    function mostrarFoto(src, srcset) {
      const img = q('.vxqa-main-img');
      if (!img || !src || img.getAttribute('src') === src) return;

      const previa = new Image();
      if (srcset) {
        previa.sizes = img.sizes;
        previa.srcset = srcset;
      }
      previa.src = src;

      img.classList.add('is-cambiando');
      window.setTimeout(() => {
        img.src = src;
        if (srcset) img.srcset = srcset;
        else img.removeAttribute('srcset');
        img.classList.remove('is-cambiando');
      }, 160);
    }

    const miniaturas = () => Array.from(raiz.querySelectorAll('.vxqa-thumb'));

    function verMiniatura(boton) {
      const lista = miniaturas();
      foto = lista.indexOf(boton);
      lista.forEach((t) => t.classList.toggle('is-active', t === boton));
      mostrarFoto(boton.dataset.src, boton.dataset.srcset);

      const tira = boton.parentElement;
      if (tira && tira.scrollWidth > tira.clientWidth) {
        tira.scrollTo({ left: boton.offsetLeft - (tira.clientWidth - boton.offsetWidth) / 2, behavior: 'smooth' });
      }
    }

    function navegar(paso) {
      const lista = miniaturas();
      if (!lista.length) return;
      const siguiente =
        foto < 0 ? (paso > 0 ? 0 : lista.length - 1) : Math.max(0, Math.min(lista.length - 1, foto + paso));
      if (siguiente === foto) return;
      verMiniatura(lista[siguiente]);
    }

    /* El mismo contenedor que usa product-form.js, para que los errores
       propios y los nativos se vean igual. Escribe en childNodes[2], el texto
       que sigue al icono, para no apilar dos mensajes. */
    function mostrarError(mensaje) {
      const caja = q('[ref="addToCartTextError"]');
      if (!caja || !mensaje) return;
      const hueco = caja.childNodes[2];
      if (hueco && hueco.nodeType === Node.TEXT_NODE) hueco.textContent = mensaje;
      else caja.appendChild(document.createTextNode(mensaje));
      caja.classList.remove('hidden');
      window.clearTimeout(relojError);
      relojError = window.setTimeout(() => caja.classList.add('hidden'), 6000);
    }

    function pintarVariante(v) {
      variante = v;
      marcarOpciones();

      const agregar = q('[data-vxqa-agregar]');
      const etiqueta = agregar ? agregar.querySelector('.vxqa-add-label') : null;
      const comprar = q('[data-vxqa-comprar]');
      const deseo = q('[data-vxqa-deseo]');
      const ocupado = !!formulario && formulario.dataset.vxComprando === '1';

      /* Una combinación que no existe: el formulario conserva la variante
         anterior, así que no se deja pulsar nada. */
      if (!v) {
        if (agregar) agregar.disabled = true;
        if (etiqueta) etiqueta.textContent = textos.noDisponible || '';
        if (comprar && !ocupado) comprar.disabled = true;
        if (deseo) deseo.disabled = true;
        return;
      }

      if (campoId) campoId.value = String(v.id);

      const precio = q('[data-vxqa-precio]');
      const precioBoton = q('[data-vxqa-precio-boton]');
      if (precio) precio.innerHTML = precioHtml(v);
      if (precioBoton) precioBoton.innerHTML = precioHtml(v);

      const oferta = v.compare_at_price > v.price;
      const tachado = q('[data-vxqa-tachado]');
      const descuento = q('[data-vxqa-descuento]');
      if (tachado) {
        tachado.hidden = !oferta;
        if (oferta) tachado.innerHTML = tachadoHtml(v);
      }
      if (descuento) {
        descuento.hidden = !oferta;
        if (oferta) descuento.innerHTML = descuentoHtml(v);
      }

      if (agregar) agregar.disabled = !v.available;
      if (etiqueta) etiqueta.textContent = v.available ? textos.agregar : textos.agotado;
      if (comprar && !ocupado) comprar.disabled = !v.available;
      if (deseo) {
        deseo.disabled = false;
        marcarDeseo(!!window.VXWish && window.VXWish.has(v.id), false);
      }

      raiz.querySelectorAll('[data-vxqa-enlace]').forEach((enlace) => {
        if (v.url) enlace.setAttribute('href', v.url);
      });

      /* La foto de la variante no está entre las miniaturas, igual que en la
         ficha: ninguna queda marcada. */
      if (v.imagen) {
        foto = -1;
        miniaturas().forEach((t) => t.classList.remove('is-active'));
        mostrarFoto(v.imagen, srcsetDe(v.imagen));
      }
    }

    function elegir(opcion) {
      const sel = opcion.closest('.vxqa-sel');
      if (!sel) return;

      sel.querySelectorAll('.vxqa-sel-opt').forEach((o) => {
        const activa = o === opcion;
        o.classList.toggle('is-active', activa);
        o.setAttribute('aria-selected', activa ? 'true' : 'false');
      });
      const valor = sel.querySelector('.vxqa-sel-value');
      if (valor) valor.textContent = opcion.dataset.vxqaValor || '';

      cerrarSelectores(raiz, true);
      opciones[sel.dataset.vxqaOpcion] = opcion.dataset.vxqaValor;
      pintarVariante(buscar(opciones));
    }

    function alternar(sel) {
      const abierto = sel.classList.contains('is-open');
      cerrarSelectores(raiz, false);
      if (abierto) return;

      sel.classList.add('is-open');
      const boton = sel.querySelector('[data-vxqa-abrir]');
      if (boton) boton.setAttribute('aria-expanded', 'true');

      /* El diálogo recorta lo que se sale. Si abajo no cabe y arriba sí, la
         lista se abre hacia arriba; si no cabe en ningún lado, se desplaza
         la columna hasta que se vea. */
      const panel = sel.querySelector('.vxqa-sel-panel');
      if (!panel) return;

      const info = q('.vxqa-info');
      const desplaza = info && getComputedStyle(info).display !== 'contents' ? info : raiz;
      const cajas = [desplaza.getBoundingClientRect()];
      if (dialogo) cajas.push(dialogo.getBoundingClientRect());
      const fondo = Math.min(window.innerHeight, ...cajas.map((c) => c.bottom)) - 8;
      const techo = Math.max(0, ...cajas.map((c) => c.top)) + 8;
      const lista = panel.getBoundingClientRect();
      if (lista.bottom <= fondo) return;

      if (sel.getBoundingClientRect().top - lista.height - 6 >= techo) {
        sel.classList.add('vxqa-sel--arriba');
      } else {
        desplaza.scrollTop += lista.bottom - fondo;
      }
    }

    function cambiarCantidad(delta) {
      const campo = q('.vxqa-qty-input');
      if (!campo) return;
      const paso = parseInt(campo.step, 10) || 1;
      const minimo = parseInt(campo.min, 10) || 1;
      const maximo = campo.max !== '' ? parseInt(campo.max, 10) : NaN;
      let siguiente = (parseInt(campo.value, 10) || minimo) + delta * paso;
      if (siguiente < minimo) siguiente = minimo;
      if (!Number.isNaN(maximo) && siguiente > maximo) {
        siguiente = maximo;
        mostrarError(`Solo quedan ${maximo} unidades disponibles.`);
      }
      campo.value = String(siguiente);
      campo.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* «Comprar ahora». En dólares, lo mismo que la ficha: agrega con
       /cart/add.js, respetando lo que ya haya en el carrito, y va al
       checkout. En soles el checkout cobraría en dólares: abre WhatsApp
       con esta variante y la cantidad, sin tocar el carrito
       (assets/vx-pago-whatsapp.js). */
    function comprarAhora(boton) {
      if (!componente || !formulario || boton.disabled) return;

      const pago = window.VXPagoWhatsApp;
      if (pago && pago.activo()) {
        if (!variante || !variante.available) return;
        const campo = q('.vxqa-qty-input');
        pago.comprar({
          titulo: datos.titulo,
          variante: variante.title && variante.title !== 'Default Title' ? variante.title : '',
          cantidad: campo ? campo.value : 1,
          precioCents: variante.price,
          penCentimos: variante.pen,
          url: datos.url,
          variantId: variante.id,
        });
        return;
      }

      const etiqueta = boton.innerHTML;
      boton.disabled = true;
      boton.innerHTML = '<strong>Procesando…</strong>';
      formulario.dataset.vxComprando = '1';

      const restaurar = () => {
        delete formulario.dataset.vxComprando;
        boton.disabled = false;
        boton.innerHTML = etiqueta;
      };

      fetch((window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(formulario),
      })
        .then((respuesta) => respuesta.json())
        .then((resultado) => {
          if (resultado && resultado.status) {
            mostrarError(resultado.description || resultado.message || '');
            restaurar();
            return;
          }
          window.location.href = datos.checkout || '/checkout';
        })
        .catch(() => {
          mostrarError('No se pudo procesar la compra. Intenta de nuevo.');
          restaurar();
        });
    }

    /* La lista de deseos vive en assets/vx-wishlist.js. El precio se guarda
       en centavos de dólar, como en la ficha: convertir es cosa de quien
       pinta. */
    function alternarDeseo() {
      const lista = window.VXWish;
      if (!lista || !variante) return;

      const id = String(variante.id);
      if (lista.has(id)) {
        lista.remove(id);
        marcarDeseo(false, false);
        return;
      }

      const img = q('.vxqa-main-img');
      lista.add({
        variantId: id,
        productId: datos.productoId,
        name: datos.titulo,
        variantName: lista.variantLabel(variante.title),
        image: img ? img.currentSrc || img.src : datos.imagen,
        priceCents: variante.price,
        currency: datos.moneda,
        handle: datos.handle,
        link: `${datos.url}?variant=${id}`,
      });
      marcarDeseo(true, true);
    }

    raiz.addEventListener('click', (evento) => {
      const objetivo = evento.target instanceof Element ? evento.target.closest('button, a') : null;
      if (!objetivo || !raiz.contains(objetivo)) return;

      if (objetivo.matches('[data-vxqa-abrir]')) {
        const sel = objetivo.closest('.vxqa-sel');
        if (sel) alternar(sel);
      } else if (objetivo.matches('.vxqa-sel-opt')) {
        elegir(objetivo);
      } else if (objetivo.matches('[data-vxqa-cantidad]')) {
        cambiarCantidad(Number(objetivo.dataset.vxqaCantidad) || 0);
      } else if (objetivo.matches('[data-vxqa-foto]')) {
        verMiniatura(objetivo);
      } else if (objetivo.matches('[data-vxqa-nav]')) {
        navegar(Number(objetivo.dataset.vxqaNav) || 0);
      } else if (objetivo.matches('[data-vxqa-comprar]')) {
        evento.preventDefault();
        comprarAhora(objetivo);
      } else if (objetivo.matches('[data-vxqa-deseo]')) {
        evento.preventDefault();
        alternarDeseo();
      }
    });

    const principal = q('.vxqa-main');
    if (principal) {
      let inicioX = 0;
      principal.addEventListener(
        'touchstart',
        (evento) => {
          inicioX = evento.touches[0].clientX;
        },
        { passive: true }
      );
      principal.addEventListener(
        'touchend',
        (evento) => {
          const dx = evento.changedTouches[0].clientX - inicioX;
          if (Math.abs(dx) > 40) navegar(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    foto = miniaturas().findIndex((t) => t.classList.contains('is-active'));
    marcarOpciones();
    if (variante) marcarDeseo(!!window.VXWish && window.VXWish.has(variante.id), false);
    const info = q('.vxqa-info');
    if (info) info.scrollTop = 0;
    raiz.scrollTop = 0;
  }

  /* Un clic fuera de un selector lo cierra, como en la ficha. */
  document.addEventListener('click', (evento) => {
    const raiz = document.querySelector(`#${MODAL} .vxqa`);
    if (!raiz || !raiz.querySelector('.vxqa-sel.is-open')) return;
    if (evento.target instanceof Element && evento.target.closest('.vxqa-sel')) return;
    cerrarSelectores(raiz, false);
  });

  /* Escape cierra primero la lista abierta y, en otra pulsación, el
     diálogo. Va en captura para llegar antes que DialogComponent. */
  document.addEventListener(
    'keydown',
    (evento) => {
      if (evento.key !== 'Escape') return;
      const abierto = document.querySelector(`#${MODAL} .vxqa-sel.is-open`);
      if (!abierto) return;
      evento.preventDefault();
      evento.stopPropagation();
      cerrarSelectores(abierto.closest('.vxqa'), true);
    },
    true
  );
})();
